import { logoUrl, activeDocTheme, docBrandName, docLegalName, docAddress, docTaxLine, docPhone, docWeb, docSlogan } from "@/lib/company";
import api from "@/lib/api";
import { qrDataUrl, encodeDocNoQr } from "@/lib/qr";

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Zorunlu belgeler için boş, yazdırılabilir A4 şablonlar. Yazdır → elle doldur/imzala → sisteme geri yükle.
// Boş şablon basılınca sistem otomatik bir Belge No üretir ve şablonun üstüne basar (izlenebilirlik).
const TEMPLATES = {
  purchase: {
    title: "SATIN ALMA SİPARİŞ FORMU",
    meta: [["Tedarikçi", ""], ["Sipariş No", ""], ["Tarih", ""], ["Teslim Deposu", ""]],
    cols: ["#", "Ürün / Malzeme", "Miktar", "Birim", "Birim Fiyat", "Tutar"],
    rows: 13,
  },
  internal: {
    title: "İÇ TEDARİK / İÇ İKMAL SİPARİŞ FORMU",
    meta: [["Sipariş Veren Mağaza", ""], ["Bağlı Depo", ""], ["Sipariş No", ""], ["Tarih", ""]],
    cols: ["#", "Ürün", "İstenen", "Birim", "Karşılanan", "Açıklama"],
    rows: 13,
  },
  transfer: {
    title: "TRANSFER / SEVK BELGESİ",
    meta: [["Gönderen Lokasyon", ""], ["Alıcı Lokasyon", ""], ["Belge No", ""], ["Tarih", ""], ["Araç / Plaka", ""], ["Şoför", ""]],
    cols: ["#", "Ürün", "Sevk Miktarı", "Birim", "Teslim Alınan", "Açıklama"],
    rows: 13,
  },
  goods_receipt: {
    title: "MAL KABUL FÖYÜ",
    meta: [["Tedarikçi / Kaynak", ""], ["Sipariş No", ""], ["Tarih", ""], ["Mal Kabul Deposu", ""]],
    cols: ["#", "Ürün", "Sipariş", "Gelen", "SKT", "Durum (Sağlam/Fire)"],
    rows: 13,
  },
  count: {
    title: "DEPO SAYIM FÖYÜ",
    meta: [["Lokasyon (Depo/Mağaza)", ""], ["Sayım No", ""], ["Tarih", ""], ["Sayan Personel", ""]],
    cols: ["#", "Ürün / SKU", "Sistem Stok", "Sayılan", "Fark", "Açıklama"],
    rows: 15,
  },
  personnel: {
    title: "PERSONEL BİLGİ / ÖZLÜK FORMU",
    fields: [
      "Ad Soyad", "T.C. Kimlik No", "Doğum Tarihi", "Telefon", "E-posta",
      "Adres", "Görev / Pozisyon", "Çalışma Lokasyonu", "İşe Giriş Tarihi",
      "Banka / IBAN", "Acil Durum Kişisi", "Acil Durum Telefon",
    ],
  },
  contacts: {
    title: "KİŞİ / CARİ BİLGİ FORMU",
    fields: [
      "Ad Soyad / Ünvan", "Kişi Türü (Müşteri / Tedarikçi / Diğer)", "Vergi Dairesi",
      "VKN / T.C. Kimlik No", "Telefon", "E-posta", "İl / İlçe", "Adres",
      "Yetkili Kişi", "IBAN / Banka", "Ödeme Vadesi (gün)", "Notlar",
    ],
  },
  finance: {
    title: "MUHASEBE GELİR / GİDER BELGESİ",
    meta: [["Belge Türü (Gelir / Gider)", ""], ["Kategori", ""], ["Tarih", ""],
           ["Ödeme Hesabı (Nakit/Banka/Çek/Senet)", ""], ["İlgili Cari (varsa)", ""], ["Düzenleyen", ""]],
    cols: ["#", "Açıklama / Kalem", "Tutar (KDV Hariç)", "KDV %", "KDV Tutarı", "Brüt Tutar"],
    rows: 8,
  },
};

// Şablon türü → backend belge-no tipi (rastgele/otomatik sistem kodu üretimi)
const KIND_TO_DOCTYPE = {
  purchase: "purchase",
  internal: "internal",
  transfer: "transfer",
  goods_receipt: "receipt",
  count: "count",
  personnel: "personnel",
  contacts: "contacts",
  finance: "finance",
};

const CSS = (th) => `
  @page { size:A4; margin:14mm 12mm; }
  * { box-sizing:border-box; }
  body { margin:0; font-family:'Segoe UI','Helvetica Neue',Arial,sans-serif; color:#0A0A0A; font-size:12px; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  .band { height:8px; background:${th.primary}; border-radius:5px 5px 0 0; overflow:hidden; }
  .band i { display:block; height:100%; width:36%; background:${th.accent}; }
  .head { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; padding:12px 2px 10px; border-bottom:2px solid ${th.primary}; }
  .logo { height:46px; width:auto; display:block; margin-bottom:5px; }
  .brand { font-size:16px; font-weight:800; color:${th.primary}; letter-spacing:.3px; }
  .legal { font-weight:700; font-size:11px; }
  .sm { font-size:10px; color:#4B5563; line-height:1.5; }
  .title { text-align:right; }
  .title h1 { font-size:19px; font-weight:800; letter-spacing:.5px; color:${th.primary}; margin:0 0 6px; }
  .title .doc-no { font-size:10px; color:#4B5563; }
  .title .doc-no b { font-size:12px; color:${th.primary}; letter-spacing:.6px; }
  .title .qr { width:80px; height:80px; margin:8px 0 0 auto; display:block; }
  .title .qrcap { font-size:8px; color:#94a3b8; margin-top:2px; text-align:right; }
  .meta { display:grid; grid-template-columns:1fr 1fr; gap:6px 22px; margin:14px 0; }
  .meta .row { display:flex; align-items:flex-end; gap:8px; font-size:11px; }
  .meta .row b { color:${th.primary}; white-space:nowrap; }
  .meta .row .fill { flex:1; border-bottom:1px dotted #94a3b8; height:16px; }
  table { width:100%; border-collapse:collapse; margin-top:4px; }
  th { background:${th.primary}; color:#fff; font-size:10.5px; text-align:left; padding:7px 8px; font-weight:600; }
  td { border:1px solid #cbd5e1; padding:0; height:26px; }
  .fieldlist { margin-top:8px; }
  .fieldlist .fr { display:flex; align-items:flex-end; gap:10px; padding:9px 0; border-bottom:1px solid #e2e8f0; font-size:12px; }
  .fieldlist .fr b { color:${th.primary}; min-width:170px; }
  .fieldlist .fr .fill { flex:1; border-bottom:1px dotted #94a3b8; height:18px; }
  .note { margin-top:14px; }
  .note b { color:${th.primary}; font-size:11px; }
  .note .box { border:1px solid #cbd5e1; border-radius:6px; height:64px; margin-top:5px; }
  .sign { display:flex; justify-content:space-between; gap:30px; margin-top:26px; }
  .sign .blk { flex:1; text-align:center; }
  .sign .line { border-top:1px solid #64748b; margin:0 6px 5px; height:44px; }
  .sign .lbl { font-size:11px; color:#334155; font-weight:600; }
  .foot { margin-top:18px; padding-top:8px; border-top:1px dotted #94a3b8; display:flex; justify-content:space-between; font-size:9.5px; color:#64748b; }
`;

const metaHtml = (meta) => `<div class="meta">${meta.map(([l]) => `<div class="row"><b>${esc(l)}:</b><span class="fill"></span></div>`).join("")}</div>`;

const tableHtml = (cols, rows) => `
  <table>
    <thead><tr>${cols.map((c, i) => `<th${i === 0 ? ' style="width:26px;text-align:center"' : ""}>${esc(c)}</th>`).join("")}</tr></thead>
    <tbody>${Array.from({ length: rows }).map((_, r) => `<tr>${cols.map((_, i) => `<td${i === 0 ? ' style="text-align:center;color:#94a3b8;font-size:10px;padding-top:6px">' + (r + 1) : ">"}</td>`).join("")}</tr>`).join("")}</tbody>
  </table>`;

const fieldsHtml = (fields) => `<div class="fieldlist">${fields.map((f) => `<div class="fr"><b>${esc(f)}</b><span class="fill"></span></div>`).join("")}</div>`;

async function fetchDocNo(kind) {
  const type = KIND_TO_DOCTYPE[kind];
  if (!type) return "";
  try {
    const { data } = await api.get(`/logistics/generate-doc-no?type=${type}`);
    return data?.doc_no || "";
  } catch (e) {
    return "";
  }
}

function buildTemplateHtml(kind, docNo, qrImg) {
  const t = TEMPLATES[kind] || TEMPLATES.transfer;
  const th = activeDocTheme();
  const body = t.fields
    ? fieldsHtml(t.fields)
    : `${metaHtml(t.meta)}${tableHtml(t.cols, t.rows)}
       <div class="note"><b>Açıklama / Notlar</b><div class="box"></div></div>`;
  const docNoLine = docNo
    ? `<div class="doc-no">Belge No: <b>${esc(docNo)}</b></div>`
    : `<div class="doc-no">Belge No: ____________________</div>`;
  return `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${esc(t.title)}${docNo ? " · " + esc(docNo) : ""}</title><style>${CSS(th)}</style></head>
  <body>
    <div class="band"><i></i></div>
    <div class="head">
      <div>
        <img class="logo" src="${logoUrl()}" alt="${esc(docBrandName())}"/>
        <div class="brand">${esc(docBrandName())}</div>
        <div class="legal">${esc(docLegalName())}</div>
        <div class="sm">${esc(docAddress())}</div>
        <div class="sm">${esc(docTaxLine())} · ${esc(docPhone())}</div>
      </div>
      <div class="title">
        <h1>${esc(t.title)}</h1>
        ${docNoLine}
        <div class="doc-no">Tarih: ______ / ______ / ________</div>
        ${qrImg ? `<img class="qr" src="${qrImg}" alt="QR"/><div class="qrcap">Taslağı yükleyince QR ile otomatik eşleşir</div>` : ""}
      </div>
    </div>
    ${body}
    <div class="sign">
      <div class="blk"><div class="line"></div><div class="lbl">Hazırlayan (Ad-Soyad / İmza)</div></div>
      <div class="blk"><div class="line"></div><div class="lbl">Onaylayan (Ad-Soyad / İmza)</div></div>
      <div class="blk"><div class="line"></div><div class="lbl">Teslim Eden / Alan (İmza)</div></div>
    </div>
    <div class="foot"><span>${esc(docWeb())} · ${esc(docSlogan())}</span><span>${esc(docBrandName())} · Sistem Belge No${docNo ? ": " + esc(docNo) : ""}</span></div>
    <script>window.onload=function(){setTimeout(function(){window.print()},350)}<\/script>
  </body></html>`;
}

export async function printBlankTemplate(kind) {
  const t = TEMPLATES[kind] || TEMPLATES.transfer;
  // Kullanıcı hareketi (click) içinde pencereyi hemen aç — popup engellenmesin
  const w = window.open("", "_blank", "width=860,height=1180");
  if (w) {
    w.document.write(`<!doctype html><meta charset="utf-8"><title>${esc(t.title)}</title><body style="font-family:'Segoe UI',Arial,sans-serif;padding:44px;color:#334155;font-size:14px">Belge No üretiliyor, şablon hazırlanıyor…</body>`);
  }
  const docNo = await fetchDocNo(kind);
  const qrImg = docNo ? await qrDataUrl(encodeDocNoQr(KIND_TO_DOCTYPE[kind] || kind, docNo), 160) : "";
  const html = buildTemplateHtml(kind, docNo, qrImg);
  if (w) {
    w.document.open();
    w.document.write(html);
    w.document.close();
    return docNo;
  }
  // Popup engellendiyse blob URL ile yeni sekmede aç (yedek)
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return docNo;
}
